import { useEffect, useRef, useCallback, useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth, isFirebaseConfigured } from '../firebase/config';
import { useAuthStore } from '../store/authStore';
import { useChildStore } from '../store/childStore';

// ── Timing constants ────────────────────────────────────────────────────────
const WARNING_MS = 60 * 60 * 1000; // 60 min — show warning banner
const TIMEOUT_MS = 65 * 60 * 1000; // 65 min — force logout (5 min grace)
const THROTTLE_MS = 30_000; // 30 s — throttle activity events

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'touchstart',
  'scroll',
] as const;

/**
 * Tracks user activity and auto-logs out after prolonged inactivity.
 * Shows a warning banner 5 minutes before logout.
 *
 * Required for apps handling children's data (Norwegian GDPR / NIST guidance).
 */
export function useInactivityTimeout() {
  const user = useAuthStore((s) => s.user);

  const [warningVisible, setWarningVisible] = useState(false);
  const [minutesLeft, setMinutesLeft] = useState(5);

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const warningRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const countdownRef = useRef<ReturnType<typeof setInterval>>(undefined);

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    setWarningVisible(false);
    clearInterval(countdownRef.current);
    try {
      if (isFirebaseConfigured()) await signOut(auth);
    } catch {
      // continue — clear local state regardless
    }
    useAuthStore.getState().setUser(null);
    useChildStore.getState().setChildren([]);
    useChildStore.getState().setActiveChild(null);
    // Navigation to /login happens via RequireAuth guard in App.tsx
  }, []);

  // ── Show warning + countdown ──────────────────────────────────────────────
  const showWarning = useCallback(() => {
    setMinutesLeft(5);
    setWarningVisible(true);

    // Tick every 60 s to update "X minutes"
    clearInterval(countdownRef.current);
    const started = Date.now();
    countdownRef.current = setInterval(() => {
      const elapsed = Date.now() - started;
      const remaining = Math.max(1, Math.ceil((5 * 60 * 1000 - elapsed) / 60_000));
      setMinutesLeft(remaining);
    }, 60_000);
  }, []);

  // ── Schedule warning + logout timers (no setState — callable from effects) ─
  const scheduleTimers = useCallback(() => {
    clearTimeout(warningRef.current);
    clearTimeout(timeoutRef.current);

    warningRef.current = setTimeout(showWarning, WARNING_MS);
    timeoutRef.current = setTimeout(logout, TIMEOUT_MS);
  }, [logout, showWarning]);

  // ── Reset everything (timers + dismiss any visible warning) ───────────────
  const resetTimers = useCallback(() => {
    clearInterval(countdownRef.current);
    setWarningVisible(false);
    scheduleTimers();
  }, [scheduleTimers]);

  // ── Public: "Stay logged in" button calls this ────────────────────────────
  const stayLoggedIn = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  // ── Wire up activity listeners ────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    // At mount no warning is visible yet, so scheduling alone matches
    // resetTimers() without the setState-in-effect
    scheduleTimers();

    // Throttled handler — prevents firing on every pixel of mouse movement
    let throttleTimer: ReturnType<typeof setTimeout> | undefined;
    const handler = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        throttleTimer = undefined;
      }, THROTTLE_MS);
      resetTimers();
    };

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, handler, { passive: true });
    }

    return () => {
      clearTimeout(warningRef.current);
      clearTimeout(timeoutRef.current);
      clearInterval(countdownRef.current);
      clearTimeout(throttleTimer);
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, handler);
      }
    };
  }, [user, resetTimers, scheduleTimers]);

  return { warningVisible, minutesLeft, stayLoggedIn };
}
